/**
 * Features/Solutions section component for Stack Forward website
 */

import React from 'react';

/**
 * Feature interface
 */
interface Feature {
  title: string;
  description: string;
  icon: string;
  benefits: string[];
}

/**
 * Features section component
 */
export const Features: React.FC = () => {
  // Features data (Top 6 solutions - we support 40+ platforms total)
  const features: Feature[] = [
    {
      title: 'ERPNext Implementation',
      description: 'Full-featured open-source ERP system for managing accounting, inventory, HR, CRM, manufacturing, and project operations.',
      icon: 'erpnext.svg',
      benefits: ['Financial Accounting', 'Inventory Management', 'Project Tracking', 'CRM & Sales'],
    },
    {
      title: 'Odoo Customization & Migrations',
      description: 'All-in-one business management suite offering modules for CRM, accounting, sales, inventory, HR, and marketing.',
      icon: 'odoo.svg',
      benefits: ['Custom Modules', 'Data Migration', 'Multi-Company Support', 'E-commerce Integration'],
    },
    {
      title: 'Frappe HR - Modern HRMS',
      description: 'Modern open-source HRMS built by Frappe, offering employee management, attendance, payroll, performance, and expense tracking.',
      icon: 'frappe.svg',
      benefits: ['Employee Records', 'Attendance Tracking', 'Payroll Processing', 'Performance Management'],
    },
    {
      title: 'Nextcloud File Collaboration',
      description: 'Self-hosted collaboration platform for file syncing, video calling, calendar, mail, and document editing with complete data privacy.',
      icon: 'nextcloud.svg',
      benefits: ['File Sync & Share', 'Team Collaboration', 'Video Conferencing', 'Document Editing'],
    },
    {
      title: 'GitLab CE DevSecOps',
      description: 'Complete DevSecOps lifecycle platform offering source control, CI/CD, issue tracking, and security features for development teams.',
      icon: 'gitlab.svg',
      benefits: ['CI/CD Pipelines', 'Container Registry', 'Security Scanning', 'Issue Tracking'],
    },
    {
      title: 'Mattermost Secure Team Chat',
      description: 'Open-source, self-hostable chat and collaboration platform designed for secure enterprise communication and team coordination.',
      icon: 'mattermost.svg',
      benefits: ['Team Messaging', 'AD/LDAP Integration', 'SSO Authentication', 'Custom Integrations'],
    },
  ];

  return (
    <section id="solutions" className="section bg-neutral-50">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Our Solutions
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            We specialize in <span className="font-semibold text-neutral-900">40+ open-source platforms</span>.
            Here are our most popular implementations — from initial setup to ongoing support, we've got you covered.
          </p>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="card group hover:shadow-lg transition-all duration-300"
            >
              <div className="card-body">
                {/* Icon */}
                <div className="w-16 h-16 mb-6 flex items-center justify-center bg-primary-100 rounded-xl group-hover:bg-primary-200 transition-colors duration-300 p-3">
                  <img
                    src={`/logos/${feature.icon}`}
                    alt={`${feature.title} logo`}
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Title */}
                <h3 className="text-xl font-semibold text-neutral-900 mb-4">
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="text-neutral-600 mb-6 leading-relaxed">
                  {feature.description}
                </p>

                {/* Benefits */}
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">
                    Key Features
                  </h4>
                  <ul className="space-y-1">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <li
                        key={benefitIndex}
                        className="flex items-center text-sm text-neutral-600"
                      >
                        <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3 flex-shrink-0"></span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Hover effect */}
                <div className="mt-6 pt-4 border-t border-neutral-200 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button
                    onClick={() => {
                      const contactSection = document.querySelector('#contact');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors duration-200"
                  >
                    Learn More →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* More solutions badge */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-col items-center gap-3 px-8 py-4 bg-white rounded-full border-2 border-primary-200 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold text-primary-600">+35</span>
              <span className="text-lg font-semibold text-neutral-900">More Solutions Available</span>
            </div>
            <p className="text-sm text-neutral-600">
              Including: Grafana, Prometheus, Keycloak, Vault, n8n, MinIO, and many more...
            </p>
          </div>
        </div>

        {/* Bottom section */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
            <h3 className="text-2xl font-bold text-neutral-900 mb-4">
              Need a Different Solution?
            </h3>
            <p className="text-neutral-600 mb-6 max-w-2xl mx-auto">
              We work with <span className="font-semibold text-neutral-900">40+ open-source platforms</span> across
              business management, collaboration, security, DevOps, analytics, and more. Whether you need help with
              a specific tool or a complete technology stack transformation, we've got you covered.
            </p>
            <button
              onClick={() => {
                const contactSection = document.querySelector('#contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="btn-primary"
            >
              Discuss Your Requirements
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
